﻿import * as THREE from 'three';
import { publicAssetUrl } from '../utils/publicAssetUrl.ts';
import { loadBitmapTexture } from '../utils/textureLoad.ts';

export type TextureSet = {
  albedo: THREE.Texture;
  normal: THREE.Texture;
  roughness: THREE.Texture;
  ao?: THREE.Texture;
  height?: THREE.Texture;
  edgeMask?: THREE.Texture;
  rutMask?: THREE.Texture;
};

export type TerrainBlendTextureSet = {
  meadow: TextureSet;
  dense: TextureSet;
  dry: TextureSet;
};

type TerrainBlendTextureUrls = Partial<
  Record<'albedo' | 'normal' | 'roughness' | 'ao' | 'height', string>
>;

export class RoadTextureLoader {
  private readonly maxAnisotropy: number;

  constructor(maxAnisotropy: number) {
    this.maxAnisotropy = maxAnisotropy;
  }

  async loadRoadTextures(): Promise<TextureSet> {
    const base = publicAssetUrl('assets/textures/roads/medieval_dirt');
    const [albedo, normal, roughness, ao, height, edgeMask, rutMask] = await Promise.all([
      this.load(`${base}/albedo.png`, true),
      this.load(`${base}/normal.png`, false),
      this.load(`${base}/roughness.png`, false),
      this.load(`${base}/ao.png`, false),
      this.load(`${base}/height.png`, false),
      this.load(`${base}/edge_mask.png`, false),
      this.load(`${base}/rut_mask.png`, false),
    ]);
    return { albedo, normal, roughness, ao, height, edgeMask, rutMask };
  }

  async loadBridgeTextures(): Promise<TextureSet> {
    const base = publicAssetUrl('assets/textures/roads/wood_logs');
    const [albedo, normal, roughness, ao, height, edgeMask] = await Promise.all([
      this.load(`${base}/albedo.png`, true),
      this.load(`${base}/normal.png`, false),
      this.load(`${base}/roughness.png`, false),
      this.load(`${base}/ao.png`, false),
      this.load(`${base}/height.png`, false),
      this.load(`${base}/edge_mask.png`, false),
    ]);
    return { albedo, normal, roughness, ao, height, edgeMask };
  }

  async loadTerrainTextures(): Promise<TextureSet> {
    const base = publicAssetUrl('assets/textures/terrain/manor_grass_blend');
    const wrapping = THREE.MirroredRepeatWrapping;
    const [albedo, normal, roughness, ao, height] = await Promise.all([
      this.load(`${base}/albedo.png`, true, wrapping),
      this.load(`${base}/normal.png`, false, wrapping),
      this.load(`${base}/roughness.png`, false, wrapping),
      this.load(`${base}/ao.png`, false, wrapping),
      this.load(`${base}/height.png`, false, wrapping),
    ]);
    return { albedo, normal, roughness, ao, height };
  }

  async loadTerrainBlendTextures(): Promise<TerrainBlendTextureSet> {
    const [meadow, dense, dry] = await Promise.all([
      this.loadTerrainBlendSet(publicAssetUrl('assets/textures/terrain/gorski_meadow_grass_v1')),
      this.loadTerrainBlendSet(publicAssetUrl('assets/textures/terrain/gorski_dense_grass_v1')),
      this.loadTerrainBlendSet(
        publicAssetUrl('assets/textures/terrain/gorski_dry_grass_v1'),
        {
          albedo: publicAssetUrl('assets/textures/terrain/gorski_dry_grass_v1/snow_leaf_albedo_atlas.png'),
          roughness: publicAssetUrl('assets/textures/terrain/gorski_dry_grass_v1/snow_leaf_hrao_atlas.png'),
        },
      ),
    ]);
    return { meadow, dense, dry };
  }

  private async loadTerrainBlendSet(
    base: string,
    urls: TerrainBlendTextureUrls = {},
  ): Promise<TextureSet> {
    const [albedo, normal, roughness, ao, height] = await Promise.all([
      this.load(urls.albedo ?? `${base}/albedo.png`, true, THREE.MirroredRepeatWrapping),
      this.load(urls.normal ?? `${base}/normal.png`, false, THREE.MirroredRepeatWrapping),
      this.load(urls.roughness ?? `${base}/roughness.png`, false, THREE.MirroredRepeatWrapping),
      this.load(urls.ao ?? `${base}/ao.png`, false, THREE.MirroredRepeatWrapping),
      this.load(urls.height ?? `${base}/height.png`, false, THREE.MirroredRepeatWrapping),
    ]);
    return { albedo, normal, roughness, ao, height };
  }

  private load(url: string, srgb: boolean, wrapping: THREE.Wrapping = THREE.RepeatWrapping): Promise<THREE.Texture> {
    return loadBitmapTexture(url, this.maxAnisotropy, { srgb, wrapping });
  }
}
